/**
 * ttApi.js — client for the Titan Treasure reporting API (DS-238).
 *
 * Backend: onboarding_services/routes/tt_reporting_route.py, mounted under
 * the analytics service at /analytics/tt/*. The tenant comes from the
 * X-Tenant-Realm header; the backend only answers for Titan Treasure.
 */
import { useEffect, useState } from "react";
import { getCurrentRealm } from "../../keycloak";

// Its own variable on purpose: the Docker build defaults
// REACT_APP_ANALYTICS_API_BASE_URL to the orchestrator (:9090), which does not
// serve /analytics. Local dev: REACT_APP_TT_REPORTING_API_BASE=http://localhost:8080/analytics
const ANALYTICS_API_BASE =
  process.env.REACT_APP_TT_REPORTING_API_BASE ||
  "https://coral-app-2-4y6qg.ondigitalocean.app/analytics";

export const TT_API_BASE = `${ANALYTICS_API_BASE.replace(/\/+$/, "")}/tt`;

// Realms that see the Titan Treasure dashboard, compared after lower-casing
// and dropping spaces, "_" and "-". Override with REACT_APP_TT_REPORTING_REALMS.
const TT_REALMS = (process.env.REACT_APP_TT_REPORTING_REALMS || "titantreasure,titantreasures")
  .split(",")
  .map((r) => r.trim().toLowerCase().replace(/[\s_-]/g, ""))
  .filter(Boolean);

const activeRealm = () => getCurrentRealm() || sessionStorage.getItem("active_realm") || "";

export const isTitanReportingRealm = () =>
  TT_REALMS.includes(activeRealm().toLowerCase().replace(/[\s_-]/g, ""));

// Windowed panels go back at most 7 days; User investigation is always all time.
export const WINDOW_OPTIONS = [
  { value: "1h", label: "1h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
];
export const DEFAULT_WINDOW = "7d";

const accessToken = () =>
  localStorage.getItem("access_token") || sessionStorage.getItem("access_token") || null;

/** GET /analytics/tt/<path>; resolves to the response's `data`. */
export async function ttGet(path, params = {}, signal) {
  // The base may be relative ("/analytics") when the page and API share a host.
  const url = new URL(`${TT_API_BASE}/${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
  });
  const headers = { "X-Tenant-Realm": activeRealm() };
  const token = accessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url.toString(), { headers, signal });
  let body = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error page */
  }
  if (!res.ok || body?.status === "error") {
    const err = new Error(body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body?.data;
}

/**
 * useTTQuery("summary", { window }) → { data, loading, error }.
 * Refetches whenever `path` or any param changes; pass `enabled: false` to
 * hold off (e.g. until an email is known).
 */
export function useTTQuery(path, params = {}, { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  const key = JSON.stringify([path, params]);

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    const [p, q] = JSON.parse(key);
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    ttGet(p, q, ctrl.signal)
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((err) => {
        if (err.name !== "AbortError") setState({ data: null, loading: false, error: err });
      });
    return () => ctrl.abort();
  }, [key, enabled]);

  return state;
}
